import { useEffect, useState } from 'react';

import { EntityRefUtil } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { getActiveContributorsQueryOptions } from '@/queries/users';
import { ApiService } from '@/services/api-v2';

import { Combobox } from '../ui/combobox';
import { useToast } from '../ui/toaster';

type ShareDialogProps = { url: string; open: boolean; onOpenChange: (open: boolean) => void };

// Multi-select share to active Gearbelt Contributors.
type ContributorOption = { label: string; value: string; phone: string | null };

const shareFormSchema = z.object({
  // { id, phone } pairs (not just ids) so onSubmit reads phones straight from
  // validated form state instead of re-deriving from the live query closure.
  recipients: z.array(z.object({ id: z.string(), phone: z.string().regex(/^\+\d{1,15}$/) })).min(1),
  url: z.string().url(),
});

type ShareFormSchemaType = z.infer<typeof shareFormSchema>;

export const ShareDialog = ({ url, open, onOpenChange }: ShareDialogProps) => {
  const [isSending, setIsSending] = useState(false);
  const toast = useToast();

  const form = useForm<ShareFormSchemaType>({
    resolver: zodResolver(shareFormSchema),
    defaultValues: { recipients: [], url },
  });

  const { data: contributors, isError } = useQuery(getActiveContributorsQueryOptions());

  const options: ContributorOption[] =
    contributors?.map((contributor) => ({
      label: EntityRefUtil.createUserFullNameRef(contributor),
      value: contributor.id,
      phone: contributor.phone,
    })) ?? [];

  // Radix keeps dialog content mounted while closed; reset so a prior selection
  // doesn't reappear pre-checked on the next open.
  useEffect(() => {
    if (!open) {
      form.reset({ recipients: [], url });
    }
  }, [open, url, form]);

  const onSubmit = async ({ recipients, url }: ShareFormSchemaType) => {
    setIsSending(true);
    try {
      const results = await Promise.allSettled(recipients.map(({ phone }) => ApiService.SendInspectionLinkToMobile({ phone, url })));
      const failed = results.filter((result) => result.status === 'rejected').length;
      if (failed === 0) {
        toast.success('Shared successfully');
      } else {
        toast.error(`Couldn't share with ${failed} of ${recipients.length} contributor(s)`);
      }
      if (failed < recipients.length) {
        onOpenChange(false);
      }
    } finally {
      setIsSending(false);
    }
  };

  const recipients = form.watch('recipients');
  const emptyMessage = isError ? 'Could not load contributors — please try again' : 'No active Gearbelt Contributors in this workspace';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-11/12 rounded-md sm:max-w-lg">
        <DialogHeader>
          <DialogTitle hideCloseButton>Share URL</DialogTitle>
          <DialogDescription className="text-secondary-text text-left">Share the URL with active Gearbelt Contributors</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="recipients"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Select Gearbelt Contributors</FormLabel>
                  <FormControl>
                    <Combobox
                      aria-label="Select Gearbelt Contributors"
                      multiple
                      options={options.map((option) => ({ value: option.value, label: option.label, disabled: !option.phone }))}
                      placeholder="Select Gearbelt Contributors"
                      emptyMessage={emptyMessage}
                      value={field.value.map((recipient) => recipient.id)}
                      onChange={(ids) =>
                        field.onChange(
                          ids.flatMap((id) => {
                            const option = options.find((candidate) => candidate.value === id);
                            return option?.phone ? [{ id, phone: option.phone }] : [];
                          }),
                        )
                      }
                      renderItem={(option) => {
                        const phone = options.find((candidate) => candidate.value === option.value)?.phone;
                        return (
                          <div>
                            <div>{option.label}</div>
                            <div className="text-grey-5">{phone ?? 'No phone number'}</div>
                          </div>
                        );
                      }}
                      error={!!form.formState.errors.recipients}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="success" disabled={recipients.length === 0 || isSending} onClick={form.handleSubmit(onSubmit)}>
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
